import React from 'react';
import styled from 'styled-components';
import {WarningIcon} from '@/web-components/design-icons';
import ChevronRight from '../../../../images/chevrons/ChevronRight';
import {WarningIconVariant} from '@/web-components/design-icons/WarningIcon';
import {RagStatus} from '@/forecast-app/shared/components/status/StatusWrapper';

interface StatusIndicatorWrapperProps {
	color: string;
	withMargin?: boolean;
	active?: boolean;
	onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

const StatusIndicatorWrapper = styled.div<StatusIndicatorWrapperProps>`
	display: inline-flex;
	height: 24px;
	align-items: center;
	padding: 6px 10px;
	border: 1px solid #fff;
	border-color: ${props => props.color};
	border-radius: 20px;
	font-size: 10px;
	color: ${props => props.color};
	white-space: nowrap;
	margin-right: ${props => props.withMargin && '8px'};
	background-color: #fff;
	outline: none;
	cursor: ${props => props.onClick && 'pointer'};
	box-shadow: ${props => props.active && '0px 0px 0px 1px white, 0px 0px 0px 3px #8843EF'};
	&:focus {
		box-shadow: ${props => !props.active && props.onKeyDown && '0px 0px 0px 1px white, 0px 0px 0px 3px #008BFA'};
	}
`;

interface DotProps {
	color: string;
}
const Dot = styled.div<DotProps>`
	width: 8px;
	height: 8px;
	background-color: ${props => props.color};
	border-radius: 50%;
	margin-right: 6px;
`;

const ChevronWrapper = styled.div`
	display: flex;
	margin-left: 8px;
`;

export const convertRagStatusToWarningVariant = (statusColor: RagStatus): WarningIconVariant => {
	switch (statusColor) {
		case 'RED':
			return WarningIcon.VARIANT.DANGER;
		case 'AMBER':
			return WarningIcon.VARIANT.WARNING;
		default:
			return WarningIcon.VARIANT.NO_WARNING;
	}
};

export const convertStatusColorToWarningVariant = (statusColor: string): WarningIconVariant => {
	switch (statusColor) {
		case '#ff3300':
			return WarningIcon.VARIANT.DANGER;
		case '#ffc107':
			return WarningIcon.VARIANT.WARNING;
		default:
			return WarningIcon.VARIANT.NO_WARNING;
	}
};

export const getTextFromVariant = (variant: WarningIconVariant): string => {
	switch (variant) {
		case WarningIcon.VARIANT.DANGER:
			return 'Off track';
		case WarningIcon.VARIANT.WARNING:
			return 'At risk';
		default:
			return 'On track';
	}
};

interface BaseStatusIndicatorProps {
	active?: boolean;
	showChevron?: boolean;
	withMargin?: boolean;
	onSelect?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

interface WarningStatusIndicatorProps extends BaseStatusIndicatorProps {
	warning: WarningIconVariant;
	projectStatusColor?: never;
	fromRagStatus?: never;
}

interface ColorStatusIndicatorProps extends BaseStatusIndicatorProps {
	warning?: never;
	fromRagStatus?: false;
	projectStatusColor: string;
}

interface RagStatusIndicatorProps extends BaseStatusIndicatorProps {
	warning?: never;
	fromRagStatus: true;
	projectStatusColor: RagStatus;
}

type StatusIndicatorProps = WarningStatusIndicatorProps | ColorStatusIndicatorProps | RagStatusIndicatorProps;

export const StatusIndicator = ({
	warning,
	projectStatusColor,
	active,
	fromRagStatus,
	showChevron,
	withMargin = true,
	onSelect,
}: StatusIndicatorProps): React.ReactElement => {
	const onIndicatorKeyDown = (keyboardEvent: React.KeyboardEvent<HTMLDivElement>) => {
		if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
			keyboardEvent.stopPropagation();
			keyboardEvent.preventDefault();
			if (onSelect) {
				onSelect(keyboardEvent);
			}
		}
	};

	const onIndicatorClick = keyboardEvent => {
		if (onSelect) {
			onSelect(keyboardEvent);
		}
	};

	const warningVariant =
		warning ||
		(fromRagStatus
			? convertRagStatusToWarningVariant(projectStatusColor)
			: convertStatusColorToWarningVariant(projectStatusColor));

	return (
		<StatusIndicatorWrapper
			color={warningVariant.color}
			active={active}
			onClick={onIndicatorClick}
			onKeyDown={onIndicatorKeyDown}
			withMargin={withMargin}
			tabIndex={onSelect ? 0 : undefined}
		>
			<Dot color={warningVariant.color}></Dot>
			{getTextFromVariant(warningVariant)}
			{showChevron ? (
				<ChevronWrapper>
					<ChevronRight color={warningVariant.color} />
				</ChevronWrapper>
			) : null}
		</StatusIndicatorWrapper>
	);
};
